import React, { useState, useEffect } from 'react';
import { api } from '../../../config/api';
import './FlashcardGame.css';
import ListAndTagDropdown from '../../shared/ListAndTagDropdown';
import GameLayout from '../../shared/GameLayout';
import SentenceModal from './sentenceModal';
import NewFlashcardsModal from '../../shared/NewFlashcardsModal';

const FlashcardGame = () => {
  
  const [wordLists, setWordLists] = useState([]);
  const [selectedList, setSelectedList] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [sessionId, setSessionId] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [cards, setCards] = useState([]);
  const [totalCards, setTotalCards] = useState(0);
  const [reviewedCards, setReviewedCards] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showNewSessionForm, setShowNewSessionForm] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [sessionStats, setSessionStats] = useState(null);
  const [showSentenceModal, setShowSentenceModal] = useState(false);
  const [showNewFlashcardsModal, setShowNewFlashcardsModal] = useState(false);

  useEffect(() => {
    const fetchWordLists = async () => {
      try {
        const lists = await api.getWordLists();
        setWordLists(lists);
      } catch (err) {
        setError('Nie udało się pobrać list słówek');
      }
    };

    fetchWordLists();
  }, []);

  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        setLoading(true);
        const activeSessionStatus = await api.isActiveFlashcardSession();
        
        if (activeSessionStatus.status === 1) {
          handleContinueSession();
        } else {
          setShowNewSessionForm(true);
        }
      } catch (error) {
        console.error('Błąd podczas sprawdzania aktywnej sesji:', error);
        setShowNewSessionForm(true);
      } finally {
        setLoading(false);
      }
    };

    checkActiveSession();
  }, []);

  const handleCardFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleAnswer = async (isKnown) => {
    if (isAnimating) return;
    setIsAnimating(true);

    try {
      // Oznacz fiszkę jako znaną/nieznaną
      await api.updateFlashcardStatus(sessionId, currentCard.id, isKnown);

      currentCard.status_id = isKnown ? 2 : 1;
      
      // Aktualizuj licznik przejrzanych kart
      setReviewedCards(prev => prev + 1);
      
      // Znajdź następną kartę do pokazania (status 1 - unknown)
      const nextCard = cards.find(card => card.status_id === 3);
      
      setIsFlipped(false);

      if (nextCard) {
        setCurrentCard(nextCard);
      } else {
        // Jeśli nie ma więcej kart, pobierz statystyki sesji
        setLoading(true);
        const stats = await api.getFlashcardSessionStats(sessionId);
        setSessionStats(stats);
        setShowResults(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Błąd podczas aktualizacji statusu fiszki:', error);
      setError('Wystąpił błąd podczas aktualizacji statusu fiszki');
    } finally {
      setIsAnimating(false);
    }
  };

  const moveCardEffect = (direction) => {
    if (!currentCard) return;

    // Sprawdź czy to nie jest ostatnia karta
    const remainingCards = cards.filter(card => card.status_id === 3);
    if (remainingCards.length <= 1) {
      return;
    }
    
    // 1. Zdubluj flashcard-container i pokaż go nad obecnym
    const originalContainer = document.querySelector('.flashcard-container');
    const clonedContainer = originalContainer.cloneNode(true);
    
    // Ustaw pozycję absolutną i z-index, aby wyświetlić nad oryginalnym
    clonedContainer.style.position = 'absolute';
    clonedContainer.style.top = originalContainer.offsetTop + 'px';
    clonedContainer.style.left = originalContainer.offsetLeft + 'px';
    clonedContainer.style.width = originalContainer.offsetWidth + 'px';
    clonedContainer.style.height = originalContainer.offsetHeight + 'px';
    clonedContainer.style.zIndex = '10';
    
    // Zamień dynamiczne elementy na statyczne
    const clonedCard = clonedContainer.querySelector('.flashcard');
    if (clonedCard) {
      const clonedFront = clonedCard.querySelector('.card-front h3');
      const clonedBack = clonedCard.querySelector('.card-back h3');
      const clonedNotes = clonedCard.querySelector('.card-back p');
      
      if (clonedFront) clonedFront.textContent = currentCard.original;
      if (clonedBack) clonedBack.textContent = currentCard.translation;
      if (clonedNotes && currentCard.notes) clonedNotes.textContent = currentCard.notes;
      
      // Usuń obsługę kliknięcia
      clonedCard.removeAttribute('onclick');
    }
    
    // Dodaj klasę dla animacji
    clonedContainer.classList.add('throwing-card-left');
    
    // Dodaj do DOM
    document.body.appendChild(clonedContainer);
    
    // 2. Wyrzuć nowy kontener po 0,2 sekundy animacją za ekran
    setTimeout(() => {
      if (direction === 'left') {
        clonedContainer.style.transform = 'translateX(-60%) rotate(-10deg)';
      } else {
        clonedContainer.style.transform = 'translateX(60%) rotate(10deg)';
      }
      clonedContainer.style.opacity = '0';
      clonedContainer.style.transition = 'transform 0.4s ease-out, opacity 0.4s ease-out';
      
      // 3. Usuń nowy element po zakończeniu animacji
      setTimeout(() => {
        document.body.removeChild(clonedContainer);
      }, 500);
    }, 200);
  }

  const handleContinueSession = async () => {
    try {
      setLoading(true);
      const activeSession = await api.getActiveFlashcardSession();
      
      if (!activeSession) {
        setError('Nie znaleziono aktywnej sesji');
        setShowNewSessionForm(true);
        return;
      }
      
      setSessionId(activeSession.session_id);
      
      // Filtruj karty, aby pokazać tylko te z statusem 1 (unknown)
      const unknownCards = activeSession.cards.filter(card => card.status_id === 3);
      setCards(unknownCards);
      
      // Oblicz liczbę przejrzanych kart (status 2 lub 3)
      const reviewedCount = activeSession.cards.filter(card => card.status_id === 1 || card.status_id === 2).length;
      setReviewedCards(reviewedCount);
      
      setTotalCards(activeSession.total_cards);
      
      // Ustaw pierwszą kartę jako aktualną
      if (unknownCards.length > 0) {
        setCurrentCard(unknownCards[0]);
      } else {
        // Jeśli nie ma więcej kart, pobierz statystyki sesji
        const stats = await api.getFlashcardSessionStats(activeSession.session_id);
        setSessionStats(stats);
        setShowResults(true);
      }
      
    } catch (error) {
      console.error('Błąd podczas pobierania aktywnej sesji:', error);
      setError('Wystąpił błąd podczas pobierania aktywnej sesji');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueLearning = async () => {
    try {
      setLoading(true);
      setSessionStats(null);
      setShowResults(false);
      const activeSession = await api.createFlashcardSession(null, null,sessionId);
      setSessionId(activeSession.session_id);
      
      // Filtruj karty, aby pokazać tylko te z statusem 1 (unknown)
      const unknownCards = activeSession.cards.filter(card => card.status_id === 3);
      setCards(unknownCards);
      
      // Oblicz liczbę przejrzanych kart (status 2 lub 3)
      const reviewedCount = activeSession.cards.filter(card => card.status_id === 1 || card.status_id === 2).length;
      setReviewedCards(reviewedCount);
      
      setTotalCards(activeSession.total_cards);
      
      // Ustaw pierwszą kartę jako aktualną
      if (unknownCards.length > 0) {
        setCurrentCard(unknownCards[0]);
      } else {
        // Jeśli nie ma więcej kart, pobierz statystyki sesji
        const stats = await api.getFlashcardSessionStats(activeSession.session_id);
        setSessionStats(stats);
        setShowResults(true);
      }
      
    } catch (error) {
      console.error('Błąd podczas pobierania aktywnej sesji:', error);
      setError('Wystąpił błąd podczas pobierania aktywnej sesji');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateSession = async () => {
    try {
      await api.deactivateFlashcardSession(sessionId);
    } catch (error) {
      console.error('Błąd podczas deaktywacji sesji:', error);
      setError('Wystąpił błąd podczas deaktywacji sesji');
    }
  };

  const handleStartNewSession = () => {
    handleDeactivateSession();
    setSessionId(null);
    setSessionStats(null);
    setShowResults(false);
    setShowNewSessionForm(true);
  };

  const handleStartGame = async (e) => {
    e.preventDefault();
    if (!selectedList) return;
    
    try {
      setLoading(true);
      
      // Utwórz sesję z wszystkimi słówkami
      const response = await api.createFlashcardSession(selectedList, selectedTag);
      
      if (!response || !response.session_id) {
        throw new Error('Nieprawidłowa odpowiedź z serwera');
      }
      
      setSessionId(response.session_id);
      setCards(response.cards);
      setTotalCards(response.total_cards);
      setReviewedCards(0);
      
      // Ustaw pierwszą kartę jako aktualną
      if (response.cards.length > 0) {
        setCurrentCard(response.cards[0]);
      } else {
        setError('Brak fiszek w sesji');
      }
      
      setShowNewSessionForm(false);
    } catch (error) {
      console.error('Błąd podczas rozpoczynania nowej sesji:', error);
      setError('Nie udało się rozpocząć nowej sesji');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <GameLayout
        breadcrumbItems={[
          { label: 'Nauka', path: '/study' },
          { label: 'Fiszki' }
        ]}
      >
        <div className="text-center">
          <p>Ładowanie...</p>
        </div>
      </GameLayout>
    );
  }

  if (error) {
    return (
      <GameLayout
        breadcrumbItems={[
          { label: 'Nauka', path: '/study' },
          { label: 'Fiszki' }
        ]}
      >
        <div className="error-message">{error}</div>
      </GameLayout>
    );
  }

  if (showNewSessionForm) {
    return (
      <GameLayout
        breadcrumbItems={[
          { label: 'Nauka', path: '/study' },
          { label: 'Fiszki' }
        ]}
      >
        <div className="max-w-md w-full">
          <h2 className="text-lg font-medium mb-4 text-center">Wybierz listę słówek</h2>
          
          <form onSubmit={handleStartGame}>
            <div className="space-y-4">
              <ListAndTagDropdown
                selectedList={selectedList}
                selectedTag={selectedTag}
                onListChange={setSelectedList}
                onTagChange={setSelectedTag}
              />
            </div>

            <button
              type="submit"
              className="w-full px-3 py-1 mt-4 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              disabled={!selectedList}
            >
              Rozpocznij naukę
            </button>
          </form>
        </div>
      </GameLayout>
    );
  }

  if (showResults) {
    return (
      <GameLayout
        breadcrumbItems={[
          { label: 'Nauka', path: '/study' },
          { label: 'Fiszki' }
        ]}
      >
        <div className="max-w-md w-full">
          <h2 className="text-lg font-medium mb-3 text-center">Wyniki sesji</h2>
          <div className="mb-4">
            <p className="text-sm mb-1">Znane słówka: {sessionStats.known_count}</p>
            <p className="text-sm mb-1">Nieznane słówka: {sessionStats.unknown_count}</p>
            <p className="text-sm mb-3">Procent znanych: {sessionStats.completion_percentage}%</p>
          </div>
          <div className="flex space-x-4">
            {sessionStats.unknown_count > 0 && (
              <button
                onClick={() => {
                  handleContinueLearning();
                }}
                className="w-1/2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Kontynuuj naukę
              </button>
            )}
            <button
              onClick={() => {
                handleDeactivateSession();
                setSessionId(null);
                setSessionStats(null);
                setShowResults(false);
                setShowNewSessionForm(true);
              }}
              className={`${sessionStats.unknown_count === 0 ? 'w-full' : 'w-1/2'} px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors`}
            >
              Zagraj ponownie
            </button>
          </div>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout
      breadcrumbItems={[
        { label: 'Nauka', path: '/study' },
        { label: 'Fiszki' }
      ]}
    >
      <div className="w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <p className="text-center text-sm text-gray-600 select-none">
            {reviewedCards + 1} z {totalCards}
          </p>
          <button
            onClick={() => setShowNewFlashcardsModal(true)}
            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
          >
            Nowe fiszki
          </button>
        </div>
        <div className="progress-bar bg-gray-200 rounded-full h-2.5 mb-2">
          <div 
            className="progress bg-blue-500 h-2.5 rounded-full" 
            style={{ width: `${((reviewedCards + 1)/ totalCards) * 100}%` }}
          />
        </div>
        <div className="flashcard-container">
          {currentCard ? (
            <div 
              className={`flashcard ${isFlipped ? 'fade-in' : ''}`}
              onClick={handleCardFlip}
            >
              <div className="card-inner">
                <div className="card-front">
                  <h3 className="select-none">{currentCard.original}</h3>
                </div>
                <div className="card-back">
                  <h3 className="select-none">{currentCard.translation}</h3>
                  {currentCard.notes && <p>{currentCard.notes}</p>}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center p-4">
              <p>Brak dostępnych fiszek</p>
            </div>
          )}
        </div>

        <div className="answer-buttons flex space-x-4 mt-6 w-full md:w-auto justify-center">
          <button 
            onClick={() => {
              moveCardEffect("left"); 
              handleAnswer(false);
            }}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 transition-colors"
            disabled={isAnimating || !currentCard}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button 
            onClick={() => {
              setShowSentenceModal(true);
            }}
            className="h-8 px-3 py-1 text-xs font-medium text-white bg-primary-500 rounded-md hover:bg-primary-600 transition-colors self-center select-none"
            disabled={isAnimating || !currentCard}
          >
            Zdanie
          </button>
          <button 
            onClick={() => {
              moveCardEffect("right"); 
              handleAnswer(true);
            }}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-green-500 hover:bg-green-600 transition-colors"
            disabled={isAnimating || !currentCard}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </div>
        <SentenceModal isOpen={showSentenceModal} onClose={() => setShowSentenceModal(false)} word={currentCard} />
        <NewFlashcardsModal
          isOpen={showNewFlashcardsModal}
          onClose={() => setShowNewFlashcardsModal(false)}
          onConfirm={() => {
            setShowNewFlashcardsModal(false);
            handleStartNewSession();
          }}
        />
      </div>
    </GameLayout>
  );
};

export default FlashcardGame; 